/**
 * Tagging, from design/canvas/Tagging.src.html. Left: the player, the subclip timeline and the target
 * rows (whole asset first, then each subclip with its counts), then the spoken lines. Right: the tag panel for the selected target: what is custom to this clip (applied free tags and
 * the analysis's non-vocabulary guesses), the spoken lines in range, the brand's recent habit, then every
 * brand category in its settings order with its values alphabetical, applied ones blue in place
 * (inherited greyed), and add-your-own. An asset leaves Awaiting tagging with its first user tag.
 * Two tabs: Browse (the default), every tagged clip with a column per brand category, a page at a time,
 * and Tag clip (the clip open, or Nothing loaded with the recents).
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { AUDIO_RAMP, IMPORTS_LIST_LIMIT, PHRASE_KIND_LABELS, SNIPPET_KIND_LABELS, UPLOAD_MAX_FILES, isAudioTag, rampColour, type SnippetKind } from '@studio/contracts';
import { useComposerActions } from '@/components/composer-actions';
import { Icon } from '@/components/icons';
import { RecentFiles } from '@/components/recent-files';
import { RowMenu, type RowMenuItem } from '@/components/row-menu';
import { TopBar } from '@/components/shell';
import { StarRating, ratingMenuItem } from '@/components/star-rating';
import { AutoPill } from '@/components/auto-marker';
import { RecentModal } from '@/components/recent-modal';
import { TagAssetsModal } from '@/components/tag-assets-modal';
import { TaggedTable } from '@/components/tagged-table';
import { Tool, Toolbar } from '@/components/toolbar';
import { MiniWave } from '@/components/waveform';
import { PreviewControls } from '@/components/preview-controls';
import { useRangePlayback } from '@/hooks/use-range-playback';
import { useRatings } from '@/hooks/use-ratings';
import { getPreviewMode, setPreviewMode, type PreviewMode } from '@/lib/preview-mode';
import { clipAspect, fmtTime } from '@/lib/format';
import { queryClient, trpc } from '@/lib/trpc';

type Target = { kind: 'asset' | 'snippet' | 'phrase'; id: string };
/** Tag clip lists the clip's rows as the Analyser does, one kind per tab, in its order. */
type ListTab = 'scenes' | 'audio' | 'highlights' | 'extras';
const LIST_TABS: { key: ListTab; label: string; kind: SnippetKind | null }[] = [
  { key: 'scenes', label: 'Video scenes', kind: 'scene' },
  { key: 'audio', label: 'Audio snippets', kind: null },
  { key: 'highlights', label: 'Video highlights', kind: 'highlight' },
  { key: 'extras', label: 'Video extras', kind: 'extra' },
];

export function TaggingPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/_app/tagging' });
  // The Browse tab greets an arrival; a link naming a clip opens on that clip, with the subclip or audio
  // snippet it names selected (the Analyser's "Open in tagging"), else the whole clip.
  const [tab, setTab] = useState<'asset' | 'tagged'>(search.asset ? 'asset' : 'tagged');
  const [assetId, setAssetId] = useState<string | null>(search.asset ?? null);
  const [target, setTarget] = useState<Target | null>(
    search.phrase ? { kind: 'phrase', id: search.phrase }
      : search.snippet ? { kind: 'snippet', id: search.snippet }
        : search.asset ? { kind: 'asset', id: search.asset } : null);
  const [ownTag, setOwnTag] = useState('');
  /** The tag panel's two halves: what is seen and what is heard. */
  const [panelTab, setPanelTab] = useState<'video' | 'audio'>('video');
  /** Highlights lead: they are what the rest of the system reuses. */
  const [listTab, setListTab] = useState<ListTab>(search.phrase ? 'audio' : 'highlights');
  /** Play latched: every row plays as it is selected. Stop latched: a row holds on its first frame. */
  const [previewMode, setPreviewModeState] = useState<PreviewMode>(getPreviewMode);
  const choosePreview = (mode: PreviewMode) => {
    setPreviewModeState(mode);
    setPreviewMode(mode);
  };
  /** The preview's playhead, and whether it moves: rows light as it passes them. */
  const [clock, setClock] = useState<{ atMs: number; playing: boolean }>({ atMs: 0, playing: false });
  /** A subclip opened from the Table waits here until the clip loads, then its tab opens. */
  const openedSnippet = useRef<string | null>(search.snippet ?? null);
  const [recentOpen, setRecentOpen] = useState(false);
  /** Rows ticked in the Browse tab, and the ones sent to the tag modal. */
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [tagIds, setTagIds] = useState<string[] | null>(null);
  /** The Browse tab lists each clip's subclips beneath it. */
  const [subclips, setSubclips] = useState(false);
  const autoOpened = useRef(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const queue = useQuery(trpc.tags.queue.queryOptions());
  const analysed = useQuery(trpc.assets.imports.queryOptions({ filter: 'analysed', limit: IMPORTS_LIST_LIMIT }));
  const facets = useQuery(trpc.facets.list.queryOptions());
  const composerActions = useComposerActions();
  const detail = useQuery(trpc.assets.detail.queryOptions({ assetId: assetId ?? '' }, { enabled: assetId !== null }));
  const counts = useQuery(trpc.tags.counts.queryOptions({ assetId: assetId ?? '' }, { enabled: assetId !== null }));
  const panel = useQuery(trpc.tags.forTarget.queryOptions(
    { targetKind: target?.kind ?? 'asset', targetId: target?.id ?? '' },
    { enabled: target !== null },
  ));

  const refresh = async () => {
    await Promise.all([
      target ? queryClient.invalidateQueries({ queryKey: trpc.tags.forTarget.queryKey({ targetKind: target.kind, targetId: target.id }) }) : Promise.resolve(),
      assetId ? queryClient.invalidateQueries({ queryKey: trpc.tags.counts.queryKey({ assetId }) }) : Promise.resolve(),
      queryClient.invalidateQueries({ queryKey: trpc.tags.queue.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.tags.tagged.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.tenant.queues.queryKey() }),
    ]);
  };
  const apply = useMutation(trpc.tags.applyTag.mutationOptions({ onSuccess: refresh }));
  const remove = useMutation(trpc.tags.remove.mutationOptions({ onSuccess: refresh }));
  const applyAll = useMutation(trpc.tags.applyToAllSubclips.mutationOptions({ onSuccess: refresh }));

  /** Open a clip in Tag clip, on the whole clip or on one of its subclips. */
  const openAsset = (id: string, snippetId?: string) => {
    setAssetId(id);
    setTarget(snippetId ? { kind: 'snippet', id: snippetId } : { kind: 'asset', id });
    openedSnippet.current = snippetId ?? null;
    setTab('asset');
  };
  // Deep entry: the first queue asset loads into Tag clip when the page is first shown with nothing
  // open, without leaving the tab the user is on; once only, so Close leaves the page closed.
  useEffect(() => {
    if (autoOpened.current || !queue.data) return;
    autoOpened.current = true;
    const first = queue.data[0];
    if (!assetId && first) {
      setAssetId(first.id);
      setTarget({ kind: 'asset', id: first.id });
    }
  }, [queue.data, assetId]);

  const d = detail.data;
  // Only what is switched on in the Analyser is here: a row switched off there is out of the index.
  const kept = d?.snippets.filter((s) => !s.excluded) ?? [];
  const phrasesOn = (d?.phrases ?? []).map((p, i, all) => ({ ...p, colour: rampColour(i, all.length, AUDIO_RAMP) })).filter((p) => !p.excluded);
  const rowsOf = (t: (typeof LIST_TABS)[number]) => (t.kind ? kept.filter((s) => s.kind === t.kind) : []);
  const countOf = (t: (typeof LIST_TABS)[number]) => (t.kind ? rowsOf(t).length : phrasesOn.length);
  const shownTab = LIST_TABS.find((t) => t.key === listTab)!;
  const ratings = useRatings(d ? [
    { kind: 'asset' as const, id: d.id },
    ...kept.map((s) => ({ kind: 'snippet' as const, id: s.id })),
    ...phrasesOn.map((p) => ({ kind: 'phrase' as const, id: p.id })),
  ] : []);
  /** A row's stars, and the same choice in its menu. */
  const starsOf = (t: Target) => <StarRating rating={ratings.ratingOf(t)} onRate={(n) => ratings.rate(t, n)} />;
  const rateItem = (t: Target) => ratingMenuItem(ratings.ratingOf(t), (n) => ratings.rate(t, n));
  useEffect(() => {
    const s = openedSnippet.current && d?.snippets.find((x) => x.id === openedSnippet.current);
    if (!s) return;
    openedSnippet.current = null;
    const t = LIST_TABS.find((x) => x.kind === s.kind);
    if (t) setListTab(t.key);
  }, [d]);
  const playback = useRangePlayback(videoRef);
  /** A target's range on the clip: the whole clip, a subclip, or an audio snippet. */
  const rangeOf = (t: Target | null): [number, number] | null => {
    if (!t || !d) return null;
    if (t.kind === 'asset') return [0, d.durationMs ?? 0];
    if (t.kind === 'snippet') {
      const s = d.snippets.find((x) => x.id === t.id);
      return s ? [s.inMs, s.outMs] : null;
    }
    const p = phrasesOn.find((x) => x.id === t.id);
    return p ? [p.startMs, p.endMs] : null;
  };
  /**
   * A row selected plays its own range, first frame to last, so what it holds is seen as it is tagged;
   * with Stop latched it only shows its first frame.
   */
  const selectTarget = (t: Target) => {
    setTarget(t);
    setPanelTab(t.kind === 'phrase' ? 'audio' : 'video');
    const range = rangeOf(t);
    if (!range) return;
    if (previewMode === 'play') playback.playRange(range[0], range[1]);
    else playback.cue(range[0]);
  };
  /**
   * While the whole clip plays (or has been played to a point), the rows the playhead is inside light up,
   * on whichever tab is open; an unplayed clip at its first frame lights nothing.
   */
  const live = (fromMs: number, untilMs: number) =>
    target?.kind === 'asset' && (clock.playing || clock.atMs > 0) && clock.atMs >= fromMs && clock.atMs < untilMs;
  const adjustInAnalyser = (rowId: string) => {
    if (assetId) void navigate({ to: '/analyse', search: { asset: assetId, snippet: rowId } });
  };
  const selectedSnippet = target?.kind === 'snippet' ? d?.snippets.find((s) => s.id === target.id) ?? null : null;
  const selectedPhrase = target?.kind === 'phrase' ? phrasesOn.find((p) => p.id === target.id) ?? null : null;
  const phraseHeading = (p: { title: string | null; kind: string }) => p.title ?? PHRASE_KIND_LABELS[p.kind] ?? p.kind;
  // Applied tags that are brand values light up in their categories; the rest are custom to this clip,
  // split between the Video and Audio tabs by what they describe.
  const appliedByValue = new Map((panel.data?.applied ?? []).flatMap((a) => (a.facetValueId ? [[a.facetValueId, a] as const] : [])));
  // A still has no sound, so its tags are always the picture's.
  const shownPanel = d?.mediaKind === 'image' ? 'video' : panelTab;
  const onTab = (label: string) => isAudioTag(label) === (shownPanel === 'audio');
  const customApplied = (panel.data?.applied ?? []).filter((a) => !a.facetValueId && onTab(a.label));
  const customSuggested = (panel.data?.custom ?? []).filter((c) => onTab(c.label));
  const frequent = (panel.data?.frequent ?? []).filter((f) => onTab(f.label));

  const addOwn = (e: FormEvent) => {
    e.preventDefault();
    const text = ownTag.trim().replace(/,$/, '');
    if (!text || !target) return;
    apply.mutate({ targetKind: target.kind, targetId: target.id, freeText: text });
    setOwnTag('');
  };

  const closeClip = () => {
    setAssetId(null);
    setTarget(null);
  };

  /**
   * The next clip, always here in Tagging: the next one waiting to be tagged, else the next analysed clip
   * after this one in library order (newest first). Past the last there is nothing next, and the clip closes.
   */
  const nextClipId = (): string | null => {
    const waiting = (queue.data ?? []).find((q) => q.id !== assetId);
    if (waiting) return waiting.id;
    const clips = analysed.data ?? [];
    const at = clips.findIndex((a) => a.id === assetId);
    return clips[at + 1]?.id ?? null;
  };
  const loadNext = () => {
    const id = nextClipId();
    if (id) openAsset(id);
    else closeClip();
  };

  return (
    <>
      <TopBar title="Tagging">
        <div className="seg upload-tabs">
          <button type="button" className={tab === 'tagged' ? 'on' : ''} onClick={() => setTab('tagged')}>Browse</button>
          <button type="button" className={tab === 'asset' ? 'on' : ''} onClick={() => setTab('asset')}>Tag clip</button>
        </div>
        {tab === 'tagged' && (
          <button type="button" className="tick" aria-pressed={subclips} onClick={() => setSubclips(!subclips)}>
            <i className={subclips ? 'on' : ''}>{subclips && <svg viewBox="0 0 16 16"><path d="m3.5 8.5 3 3 6-7" /></svg>}</i>
            Show subclips
          </button>
        )}
        {/* After the switcher, so the switcher holds its place whichever tab is showing. */}
        {d && tab === 'asset' && <span className="sub crumb">{d.name}</span>}
        <div className="right">
          {tab === 'tagged' && picked.size > 0 && (
            <>
              <button type="button" className="btn quiet" onClick={() => setPicked(new Set())}>Clear</button>
              <button type="button" className="btn primary" disabled={picked.size > UPLOAD_MAX_FILES}
                title={picked.size > UPLOAD_MAX_FILES ? `Up to ${UPLOAD_MAX_FILES} clips at a time` : undefined}
                onClick={() => setTagIds([...picked])}>Tag {picked.size}…</button>
            </>
          )}
          {tab === 'asset' && panel.data && panel.data.applied.some((a) => a.source === 'detected' || a.source === 'suggested') && <AutoPill>Auto · suggestions applied</AutoPill>}
          {d && tab === 'asset' && (
            <>
              <button type="button" className="btn" title="Open this clip in the Analyser"
                onClick={() => void navigate({ to: '/analyse', search: { asset: d.id } })}>Open in the Analyser</button>
              <button type="button" className="btn" title="Send every kept snippet to the Composer's working draft"
                onClick={() => composerActions.sendAsset(d.id)}>Send to Composer</button>
            </>
          )}
          {selectedSnippet && (
            <button type="button" className="btn" disabled={applyAll.isPending} onClick={() => applyAll.mutate({ snippetId: selectedSnippet.id })}>
              Apply to all subclips
            </button>
          )}
        </div>
      </TopBar>
      <section className="content tagging-content">
        {d && tab === 'asset' && (
          <div className="toolstrip page-toolstrip">
            <Toolbar>
              <div className="tool-group">
                <span className="tool-group-title">File</span>
                <div className="row tool-group-row">
                  <Tool icon="upload" label="Upload" title="Upload new clips" onClick={() => void navigate({ to: '/upload' })} />
                  <Tool icon="folder" label="Load" title="Pick a clip to tag" onClick={() => void navigate({ to: '/browse', search: { pick: 'tagging' } })} />
                  <Tool icon="redo" label="Next" title="Open the next clip to tag, else the next clip in the library; tags save as you apply them" onClick={loadNext} />
                  <Tool icon="clock" label="Recent" title="Recently processed clips" onClick={() => setRecentOpen(true)} />
                  <Tool icon="close" label="Close" title="Close this clip; every tag is already saved" onClick={closeClip} />
                </div>
              </div>
            </Toolbar>
          </div>
        )}
        {/* The selection outlives the operation (Dax, 2026-10-01): a careful pick is cleared only by a person. */}
        {tagIds && <TagAssetsModal assetIds={tagIds} onClose={() => setTagIds(null)} />}
        {recentOpen && (
          <RecentModal currentId={assetId} onOpen={openAsset} onClose={() => setRecentOpen(false)}
            onLibrary={() => void navigate({ to: '/library' })} />
        )}
        {tab === 'tagged' ? (
          <TaggedTable onOpen={openAsset} subclips={subclips} picked={picked} setPicked={setPicked} />
        ) : !assetId ? (
          <div className="empty-start tag-start">
            Nothing loaded.
            <div className="row tagq-empty-row">
              <Link to="/upload" className="btn lg btn-iconed"><Icon name="upload" /> Upload new clips</Link>
              <button type="button" className="btn primary lg btn-iconed" onClick={() => void navigate({ to: '/browse', search: { pick: 'tagging' } })}><Icon name="folder" /> Browse</button>
            </div>
            <RecentFiles from="tagging" onOpen={openAsset} />
          </div>
        ) : !d ? (
          <div className="empty"><div className="spinner" aria-label="Loading" /></div>
        ) : (
          <>
            <div className="tag-left">
              {d.mediaKind === 'image' ? (
                // A still is its picture and its description; there is no timeline to play or cut.
                <div className="card tag-player-card">
                  <div className={`tag-video-box ar-${clipAspect(d.width, d.height).replace(':', '-')}`}>
                    <img className="tag-video" src={d.posterUrl ?? undefined} alt={d.name} />
                  </div>
                  {d.stillDescription && <div className="tag-player-foot sub">{d.stillDescription}</div>}
                </div>
              ) : (
              <div className="card tag-player-card">
                <div className={`tag-video-box ar-${clipAspect(d.width, d.height).replace(':', '-')}`}>
                  <video ref={videoRef} className="tag-video" src={d.proxyUrl ?? undefined} poster={d.posterUrl ?? undefined} playsInline preload="metadata"
                    onClick={playback.togglePause} />
                </div>
                <div className="tag-player-foot">
                  <PreviewControls videoRef={videoRef} durationMs={d.durationMs ?? 0} mode={previewMode}
                    onPlay={() => {
                      choosePreview('play');
                      const range = rangeOf(target);
                      if (range) playback.playRange(range[0], range[1]);
                    }}
                    onPause={playback.togglePause}
                    onStop={() => {
                      choosePreview('stop');
                      playback.cue(rangeOf(target)?.[0] ?? 0);
                    }}
                    onClock={(atMs, playing) => setClock({ atMs, playing })} />
                  <div className="tag-ruler mono">
                    {[0, 0.25, 0.5, 0.75, 1].map((f) => <span key={f}>{fmtTime(Math.round((d.durationMs ?? 0) * f), false)}</span>)}
                  </div>
                </div>
              </div>
              )}
              {d.mediaKind === 'image' ? (
                <div className="tag-rows">
                  <TargetRow title="Whole photo" thumb={<RowPoster url={d.posterUrl} />} times=""
                    count={counts.data?.[d.id]} on={target?.kind === 'asset'} onClick={() => setTarget({ kind: 'asset', id: d.id })}
                    rating={starsOf({ kind: 'asset', id: d.id })} />
                </div>
              ) : (<>
              <div className="tag-rows">
                <TargetRow title="Whole asset" thumb={<RowPoster url={d.posterUrl} />} times={`0:00 – ${fmtTime(d.durationMs ?? 0, false)}`}
                  count={counts.data?.[d.id]} on={target?.kind === 'asset'} onClick={() => selectTarget({ kind: 'asset', id: d.id })}
                  rating={starsOf({ kind: 'asset', id: d.id })} />
                <div className="seg upload-tabs tag-list-tabs" role="tablist" aria-label="Subclips">
                  {LIST_TABS.map((t) => (
                    <button key={t.key} type="button" role="tab" aria-selected={listTab === t.key} className={listTab === t.key ? 'on' : ''}
                      onClick={() => setListTab(t.key)}>{t.label} · {countOf(t)}</button>
                  ))}
                </div>
                {shownTab.kind ? rowsOf(shownTab).map((s) => (
                  <TargetRow key={s.id} title={s.title ?? s.role ?? SNIPPET_KIND_LABELS[s.kind as SnippetKind] ?? s.kind} subtitle={s.description ?? ''}
                    thumb={<RowPoster url={s.posterUrl} />}
                    times={`${fmtTime(s.inMs, false)} – ${fmtTime(s.outMs, false)}`}
                    count={counts.data?.[s.id]} on={target?.kind === 'snippet' && target.id === s.id} live={live(s.inMs, s.outMs)}
                    onClick={() => selectTarget({ kind: 'snippet', id: s.id })}
                    rating={starsOf({ kind: 'snippet', id: s.id })}
                    menu={[
                      rateItem({ kind: 'snippet', id: s.id }),
                      { label: 'Adjust start and end points', onClick: () => adjustInAnalyser(s.id) },
                      { label: 'Apply its tags to all subclips', onClick: () => applyAll.mutate({ snippetId: s.id }) },
                    ]} />
                )) : phrasesOn.map((p) => (
                  <TargetRow key={p.id} title={phraseHeading(p)} subtitle={p.text}
                    thumb={<span className="tag-row-wave"><MiniWave peaks={d.peaks} durationMs={d.durationMs ?? 0} inMs={p.startMs} outMs={p.endMs} colour={p.colour} regions={d.regions} /></span>}
                    times={`${fmtTime(p.startMs, false)} – ${fmtTime(p.endMs, false)}`}
                    count={counts.data?.[p.id]} on={target?.kind === 'phrase' && target.id === p.id} live={live(p.startMs, p.endMs)}
                    onClick={() => selectTarget({ kind: 'phrase', id: p.id })}
                    rating={starsOf({ kind: 'phrase', id: p.id })}
                    menu={[rateItem({ kind: 'phrase', id: p.id }), { label: 'Adjust start and end points', onClick: () => adjustInAnalyser(p.id) }]} />
                ))}
                {countOf(shownTab) === 0 && <span className="sub tag-rows-empty">None switched on in the Analyser.</span>}
              </div>
              <p className="sub tag-explainer">A subclip inherits the asset&apos;s tags and can carry its own. The words spoken inside a subclip travel with it.</p>
              </>)}
            </div>
            <div className="card tag-panel">
              <div className="row tag-panel-head">
                <div className="h2">{target?.kind === 'asset' ? (d.mediaKind === 'image' ? 'Whole photo' : 'Whole asset')
                  : selectedSnippet ? `${selectedSnippet.title ?? selectedSnippet.role ?? SNIPPET_KIND_LABELS[selectedSnippet.kind as SnippetKind]} · ${fmtTime(selectedSnippet.inMs, false)} – ${fmtTime(selectedSnippet.outMs, false)}`
                    : selectedPhrase ? `${phraseHeading(selectedPhrase)} · ${fmtTime(selectedPhrase.startMs, false)} – ${fmtTime(selectedPhrase.endMs, false)}` : ''}</div>
                {d.mediaKind !== 'image' && <div className="seg" role="tablist" aria-label="Tags about">
                  <button type="button" role="tab" aria-selected={panelTab === 'video'} className={panelTab === 'video' ? 'on' : ''} onClick={() => setPanelTab('video')}>Video</button>
                  <button type="button" role="tab" aria-selected={panelTab === 'audio'} className={panelTab === 'audio' ? 'on' : ''} onClick={() => setPanelTab('audio')}>Audio</button>
                </div>}
              </div>
              {panel.data && (
                <>
                  {(customApplied.length > 0 || customSuggested.length > 0) && (
                    <div className="sect">
                      <div className="row tag-src">
                        <span className="label">Custom to this clip</span>
                        {customSuggested.length > 1 && target && (
                          <button type="button" className="auto btn-like" onClick={() => {
                            for (const c of customSuggested) apply.mutate({ targetKind: target.kind, targetId: target.id, freeText: c.label, source: 'detected' });
                          }}>Accept all {customSuggested.length}</button>
                        )}
                      </div>
                      <div className="tag-wrap">
                        {customApplied.map((t) => <AppliedPill key={t.id} label={t.label} inherited={t.inherited} onRemove={() => remove.mutate({ tagId: t.id })} />)}
                        {customSuggested.map((c) => (
                          <button key={c.label} type="button" className="tg sug"
                            onClick={() => target && apply.mutate({ targetKind: target.kind, targetId: target.id, freeText: c.label, source: 'detected' })}>
                            + {c.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {shownPanel === 'audio' && panel.data.spoken.length > 0 && (
                    <div className="sect">
                      <div className="row tag-src"><span className="label">Spoken in this range</span><span className="sub tag-note-sm">tap a line to tag its words</span></div>
                      <div className="tag-spoken">
                        {panel.data.spoken.map((p) => (
                          <button key={p.startMs} type="button" className="tag-line"
                            onClick={() => target && apply.mutate({ targetKind: target.kind, targetId: target.id, freeText: `says · ${p.text.slice(0, 120)}` })}>
                            <span className="mono tag-line-t">{fmtTime(p.startMs, false)}</span>
                            <span className="tag-line-q">&quot;{p.text}&quot;</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {(shownPanel === 'video' || frequent.length > 0) && (
                    <div className="sect">
                      <div className="row tag-src"><span className="label">Often used by {'this brand'}</span><span className="sub tag-note-sm">most applied in the last 30 days</span></div>
                      <div className="tag-wrap">
                        {frequent.map((f) => (
                          <button key={f.label} type="button" className="tg sug"
                            onClick={() => target && apply.mutate({ targetKind: target.kind, targetId: target.id, freeText: f.label, source: 'frequent' })}>
                            + {f.label}
                          </button>
                        ))}
                        {frequent.length === 0 && <span className="sub">Nothing applied recently; this fills as the brand tags.</span>}
                      </div>
                    </div>
                  )}
                  {shownPanel === 'audio' && customApplied.length === 0 && customSuggested.length === 0 && panel.data.spoken.length === 0 && frequent.length === 0 && (
                    <span className="sub">Nothing heard in this range is tagged yet.</span>
                  )}
                  {shownPanel === 'video' && (
                    <>
                      {/* The brand's categories in its own order, values alphabetical; an applied value lights up where it sits. */}
                      {facets.data?.map((f) => ({ f, values: f.values.filter((v) => v.current || appliedByValue.has(v.id)) })).filter(({ values }) => values.length > 0).map(({ f, values }) => (
                        <div key={f.key} className="sect">
                          <div className="row tag-src"><span className="label">{f.label}</span></div>
                          <div className="tag-wrap">
                            {values.map((v) => {
                              const on = appliedByValue.get(v.id);
                              return on ? <AppliedPill key={v.id} label={v.label} inherited={on.inherited} onRemove={() => remove.mutate({ tagId: on.id })} /> : (
                                <button key={v.id} type="button" className="tg sug"
                                  onClick={() => target && apply.mutate({ targetKind: target.kind, targetId: target.id, facet: { key: f.key, value: v.label }, source: 'user' })}>
                                  + {v.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                      <form className="sect tag-own" onSubmit={addOwn}>
                        <div className="row tag-src"><span className="label">Add your own</span><span className="sub tag-note-sm">Enter or a comma adds a tag</span></div>
                        <label className="field">
                          <input value={ownTag} placeholder="seasonal · valentines" aria-label="Add a tag"
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v.endsWith(',')) {
                                const text = v.slice(0, -1).trim();
                                if (text && target) apply.mutate({ targetKind: target.kind, targetId: target.id, freeText: text });
                                setOwnTag('');
                              } else setOwnTag(v);
                            }} />
                        </label>
                      </form>
                    </>
                  )}
                  {(apply.isError || remove.isError) && <div className="error" role="alert">{apply.error?.message ?? remove.error?.message}</div>}
                </>
              )}
            </div>
          </>
        )}
      </section>
    </>
  );
}

/** An applied tag: blue with its remove button, or greyed without one when it is inherited from the whole asset. */
function AppliedPill({ label, inherited, onRemove }: { label: string; inherited: boolean; onRemove: () => void }) {
  if (inherited) return <span className="tg inh" title="Inherited from the whole asset">{label}</span>;
  return (
    <span className="tg">
      {label}
      <button type="button" className="tg-x" aria-label={`Remove ${label}`} onClick={onRemove}>×</button>
    </span>
  );
}

/** A clip's poster at row size, or an empty frame while it is drawn. */
function RowPoster({ url }: { url: string | null }) {
  return url ? <img className="lib-thumb" src={url} alt="" /> : <span className="lib-thumb empty-poster" />;
}

/** One row: its picture, a one-line title with its description beneath, its range and its tags. */
function TargetRow({ title, subtitle, thumb, times, count, on, live = false, onClick, rating, menu }: {
  title: string;
  subtitle?: string;
  thumb: ReactNode;
  times: string;
  count: { own: number; inherited: number } | undefined;
  on: boolean;
  /** The playhead is inside this row's range. */
  live?: boolean;
  onClick: () => void;
  /** Its star rating, beside the menu. */
  rating?: ReactNode;
  menu?: (RowMenuItem | null)[];
}) {
  return (
    <div className={`tag-row${on ? ' on' : ''}${live ? ' live' : ''}`}>
      <button type="button" className="tag-row-main" onClick={onClick}>
        {thumb}
        <span className="tag-row-text">
          <span className="tag-row-n" title={title}>{title}</span>
          {subtitle && <span className="sub tag-row-d" title={subtitle}>{subtitle}</span>}
        </span>
        <span className="mono sub tag-row-t">{times}</span>
        <span className="sub tag-row-c" title={count ? `${count.own} of its own${count.inherited ? `, ${count.inherited} from the whole clip` : ''}` : undefined}>
          {count ? `${count.own + count.inherited} tags` : ''}
        </span>
      </button>
      {rating}
      {menu && <RowMenu items={menu} />}
    </div>
  );
}
